import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';
import '../Styles/components/_swipercard.scss';
import { SwiperCardProps } from '../Services/CommonServices/propTypes';

const SwiperCard = (props) => {
    // Get low power mode status from Redux
    const { lowPowerModeStatus } = useSelector(state => state.device);
    const isLowPowerMode = lowPowerModeStatus?.isLowPowerMode || false;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);
    const [shouldAutoPlay, setShouldAutoPlay] = useState({});
    const [swipeDirection, setSwipeDirection] = useState(null);
    const [swipeProgress, setSwipeProgress] = useState(0);
    const [isVideoPlaying, setIsVideoPlaying] = useState({});
    const [transitionInProgress, setTransitionInProgress] = useState(false);
    const [videosLoaded, setVideosLoaded] = useState({}); // Track which videos are loaded
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const mouseStartX = useRef(0);
    const mouseEndX = useRef(0);
    const isMouseDown = useRef(false);
    const prevItemRef = useRef(null);
    const [enteringFromLeft, setEnteringFromLeft] = useState(null);
    const [grabbing, setGrabbing] = useState(false);
    const [videoMountStates, setVideoMountStates] = useState({});
    const swipePauseThreshold = 0.2; // 20% threshold for pausing videos
    const lastVideoStates = useRef({});
    const clickStartTarget = useRef(null);
    const videoPlayerRefs = useRef({});
    const cardLatchThreshold = 0.85; // Threshold when a card is considered "latched" to the top position
    const hasCardLatched = useRef(false); // Track if a card has latched during the current swipe
    const wasSwiping = useRef(false); // Track if there was swiping during this interaction
    // const [clickStartTime, setClickStartTime] = useState(0);
    const clickPosition = useRef({ x: 0, y: 0 });
    const previousSwipeDirection = useRef(null); // Track previous swipe direction to detect changes
    const wasVideoPaused = useRef(false); // Track if the video was explicitly paused during swipe
    const [hasPlayedOnce, setHasPlayedOnce] = useState({}); // Track whether each media has played at least once


    // Destructure the new props
    const { product, isSingleLook = false, sortedMedia } = props;

    // Use sortedMedia when available, otherwise fall back to product.media
    const media = sortedMedia || product?.media || [];
    
    // Modified: Don't duplicate media when there are 1 or 2 items
    const displayMedia = useMemo(() => {
        if (!Array.isArray(media)) return [];
        
        // If we have 1 or 2 items, don't duplicate them
        if (media.length === 1 || media.length === 2) {
            return [...media];
        }
        
        // For 3+ items, keep current behavior
        return media;
    }, [media]);

useEffect(() => {
  setHasPlayedOnce(prev => {
    const next = { ...prev };
    displayMedia.forEach((item, index) => {
      if (item?.type === 'Video' && next[index] === undefined) {
        next[index] = false;
      }
    });
    return next;
  });
}, [displayMedia]);

    // Initialize video playing state and handle autoplay based on currentIndex and low power mode
    useEffect(() => {
        // Create a new video playing state object
        const newVideoPlayingState = {};

        // Set all videos to not playing by default
        displayMedia.forEach((item, index) => {
            if (item.type === 'Video') {
                // Determine if this video should be playing based on its position
                // A video should play if it's at the current index (top of visible stack) and not in low power mode
                const shouldPlay = index === currentIndex && !isLowPowerMode;
                newVideoPlayingState[index] = shouldPlay;
            }
        });

        setIsVideoPlaying(newVideoPlayingState);
    }, [displayMedia, currentIndex, isLowPowerMode]);

    // Update the tracking of previous item when current index changes
    useEffect(() => {
        prevItemRef.current = currentIndex;
    }, [currentIndex]);

    // Add state and refs for tap vs swipe detection
    const touchStartTime = useRef(0);
    const [isTap, setIsTap] = useState(false);
    const minSwipeDistance = 30; // Minimum distance (in px) to consider a swipe
    const maxTapDuration = 200; // Maximum time (in ms) for a tap
    const touchStartY = useRef(0); // Track vertical position to detect scrolling
    const touchEndY = useRef(0);

    const handleTouchStart = useCallback((e) => {
        // Skip touch handling completely for single items
        if (displayMedia.length <= 1) return;
        
        // Store the target element where the touch started
        clickStartTarget.current = e.target;
        touchStartTime.current = Date.now();
        // setClickStartTime(Date.now());
        clickPosition.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        setIsTap(true); // Assume it's a tap until proven otherwise
        setIsSwiping(false); // Start with not swiping
        wasSwiping.current = false; // Reset swiping tracking
        setSwipeDirection(null);
        setSwipeProgress(0);
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    }, [displayMedia.length]);

    const handleTouchMove = useCallback((e) => {
        // Skip handling for single items
        if (displayMedia.length <= 1) return;

        touchEndX.current = e.touches[0].clientX;
        touchEndY.current = e.touches[0].clientY;

        // Calculate X and Y distance moved
        const xDiff = Math.abs(touchStartX.current - touchEndX.current);
        const yDiff = Math.abs(touchStartY.current - touchEndY.current);

        // If significant movement in any direction, it's not a tap
        if (xDiff > 10 || yDiff > 10) {
            setIsTap(false);
        }

        // Check if touch started on a control element - if so, don't trigger swipe
        const targetIsControl = 
            clickStartTarget.current?.closest('.swiper-card__play-button') || 
            clickStartTarget.current?.closest('.swiper-card__play-button-lowpower') ||
            clickStartTarget.current?.closest('.swiper-card__pause-button');
        
        if (targetIsControl) {
            return;
        }

        // Only consider as swiping if horizontal movement is greater than vertical (to avoid triggering during scrolling)
        if (xDiff > yDiff && xDiff > 10) {
            setIsSwiping(true);
            wasSwiping.current = true; // Mark that swiping occurred during this interaction
            const diff = touchStartX.current - touchEndX.current;
            const direction = diff > 0 ? 'left' : 'right';
            
            // Detect direction change
            const directionChanged = previousSwipeDirection.current !== null && 
                                     previousSwipeDirection.current !== direction;
            previousSwipeDirection.current = direction;
            
            setSwipeDirection(direction);

            // Calculate swipe progress (0 to 1)
            const progress = Math.min(1, Math.abs(diff) / 150);
            setSwipeProgress(progress);
            
            // If swiping right, calculate which item will enter from left
            if (direction === 'right') {
                const prevIndex = currentIndex === 0 ? displayMedia.length - 1 : currentIndex - 1;
                setEnteringFromLeft(prevIndex);
                
                // Check if the previous card has latched on to the top position
                if (progress > cardLatchThreshold && !hasCardLatched.current) {
                    hasCardLatched.current = true;
                    
                    // If current card is video, register that it will need to be reset
                    const mediaItem = displayMedia[currentIndex];
                    if (mediaItem?.type === 'Video') {
                        // Mark this video for complete reset when returning
                        lastVideoStates.current[currentIndex] = 'reset';
                    }
                }
            } else {
                setEnteringFromLeft(null);
            }
            
            // Current media item
            const mediaItem = displayMedia[currentIndex];
            const isCurrentVideo = mediaItem?.type === 'Video';
            
            // Precisely handle 20% threshold for video playback
            if (isCurrentVideo) {
                if (progress >= swipePauseThreshold) {
                    // Pause video when we cross the threshold
                    if (isVideoPlaying[currentIndex]) {
                        setIsVideoPlaying(prev => ({
                            ...prev,
                            [currentIndex]: false
                        }));
                        // Only mark it as 'playing before' if we haven't marked it for reset
                        if (lastVideoStates.current[currentIndex] !== 'reset') {
                            wasVideoPaused.current = true;
                            lastVideoStates.current[currentIndex] = true;
                        }
                    }
                } else if (progress < swipePauseThreshold) {
                    // Auto-resume if we go back below threshold and video was playing before
                    // If it was marked for reset, we'll handle that in the touch end handler
                    if (!isVideoPlaying[currentIndex] && 
                        (lastVideoStates.current[currentIndex] === true || wasVideoPaused.current) &&
                        !isLowPowerMode) {
                        // Direction changed OR progress returned below threshold - resume video
                        setIsVideoPlaying(prev => ({
                            ...prev,
                            [currentIndex]: true
                        }));
                        
                        // Clear the flag once we've resumed
                        if (wasVideoPaused.current && progress === 0) {
                            wasVideoPaused.current = false;
                        }
                    }
                }
            }
            
            // If swipe is almost back to origin (indicating user changed their mind)
            if (Math.abs(diff) < 15 && directionChanged) {
                // For videos, ensure we resume if it was paused during this swipe
                if (isCurrentVideo && wasVideoPaused.current && !isLowPowerMode) {
                    setIsVideoPlaying(prev => ({
                        ...prev,
                        [currentIndex]: true
                    }));
                    wasVideoPaused.current = false;
                }
            }
        }
    }, [currentIndex, displayMedia, isVideoPlaying, swipePauseThreshold, cardLatchThreshold, displayMedia.length, isLowPowerMode]);

    const nextSlide = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % displayMedia.length);
    }, [displayMedia.length]);

    const prevSlide = useCallback(() => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? displayMedia.length - 1 : prevIndex - 1
        );
    }, [displayMedia.length]);

    const handleTouchEnd = useCallback((e) => {
        // Skip handling for single items
        if (displayMedia.length <= 1) return;
        
        // Reset swipe direction tracking
        previousSwipeDirection.current = null;
        wasVideoPaused.current = false;

        const touchDuration = Date.now() - touchStartTime.current;
        const xDistance = Math.abs(touchStartX.current - touchEndX.current);

        // If it was a quick tap without much movement, don't trigger slide change
        if (isTap && touchDuration < maxTapDuration && xDistance < minSwipeDistance) {
            // This was a tap - don't do anything (video toggle is handled separately)
            setIsSwiping(false);
            setSwipeDirection(null);
            setSwipeProgress(0);
            setEnteringFromLeft(null);
            return;
        }

        // Handle swipe behavior only if it was a deliberate swipe
        if (wasSwiping.current) {
            // Don't pause all videos when swiping ends without slide change
            // Instead, we'll handle video state restoration below

            if (touchStartX.current - touchEndX.current > minSwipeDistance) {
                // Set transition flag for smooth animation
                setTransitionInProgress(true);

                // Complete left swipe animation before changing slide
                const timer = setTimeout(() => {
                    nextSlide();
                    setSwipeDirection(null);
                    setSwipeProgress(0);
                    // Reset transition flag after animation completes
                    setTimeout(() => setTransitionInProgress(false), 75);
                }, 50);
                return () => clearTimeout(timer);
            } else if (touchEndX.current - touchStartX.current > minSwipeDistance) {
                // Set transition flag for smooth animation
                setTransitionInProgress(true);

                // Complete right swipe animation before changing slide
                const timer = setTimeout(() => {
                    prevSlide();
                    setSwipeDirection(null);
                    setSwipeProgress(0);
                    // Reset transition flag after animation completes
                    setTimeout(() => setTransitionInProgress(false), 75);
                }, 50);
                return () => clearTimeout(timer);
            } else {
                // Swipe ended without changing slides - restore video state
                const mediaItem = displayMedia[currentIndex];
                if (mediaItem?.type === 'Video') {
                    // If video was playing before swipe, ensure it's still playing (unless in low power mode)
                    if (lastVideoStates.current[currentIndex] === true && !isLowPowerMode) {
                        setIsVideoPlaying(prev => ({
                            ...prev,
                            [currentIndex]: true
                        }));
                    }
                    
                    // Clear any reset flags - we're not going to reset the video
                    if (lastVideoStates.current[currentIndex] === 'reset') {
                        lastVideoStates.current[currentIndex] = true;
                    }
                }
            }
        }
        
        // Reset the card latch flag
        hasCardLatched.current = false;
        wasSwiping.current = false;
        
        // Reset states
        setIsSwiping(false);
        setIsTap(false);
        setSwipeDirection(null);
        setSwipeProgress(0);
        setEnteringFromLeft(null);
    }, [isTap, isSwiping, isVideoPlaying, minSwipeDistance, maxTapDuration, nextSlide, prevSlide, currentIndex, displayMedia, displayMedia.length, isLowPowerMode]);

    // Add mouse event handlers for desktop swiping
    const handleMouseDown = useCallback((e) => {
        // Skip mouse handling completely for single items
        if (displayMedia.length <= 1) return;
        
        // Store the target element where the click started
        clickStartTarget.current = e.target;
        // setClickStartTime(Date.now());
        clickPosition.current = { x: e.clientX, y: e.clientY };

        // Prevent default to avoid text selection during swipe
        e.preventDefault();

        isMouseDown.current = true;
        setGrabbing(true);
        setIsSwiping(false); // Start with not swiping
        wasSwiping.current = false; // Reset swiping tracking
        setSwipeDirection(null);
        setSwipeProgress(0);
        mouseStartX.current = e.clientX;
        mouseEndX.current = e.clientX;
    }, [displayMedia.length]);

    const handleMouseMove = useCallback((e) => {
        if (!isMouseDown.current || displayMedia.length <= 1) return;

        // Check if click started on a control element - if so, don't trigger swipe
        const targetIsControl = 
            clickStartTarget.current?.closest('.swiper-card__play-button') || 
            
            clickStartTarget.current?.closest('.swiper-card__play-button-lowpower') ||

            clickStartTarget.current?.closest('.swiper-card__pause-button');
        
        if (targetIsControl) {
            return;
        }

        mouseEndX.current = e.clientX;
        const diff = mouseStartX.current - mouseEndX.current;

        // Determine swipe direction
        if (Math.abs(diff) > 10) {
            setIsSwiping(true);
            wasSwiping.current = true; // Mark that swiping occurred during this interaction
            const direction = diff > 0 ? 'left' : 'right';
            
            // Detect direction change
            const directionChanged = previousSwipeDirection.current !== null && 
                                     previousSwipeDirection.current !== direction;
            previousSwipeDirection.current = direction;
            
            setSwipeDirection(direction);

            // Calculate swipe progress (0 to 1)
            const progress = Math.min(1, Math.abs(diff) / 150);
            setSwipeProgress(progress);
            
            // If swiping right, calculate which item will enter from left
            if (direction === 'right') {
                const prevIndex = currentIndex === 0 ? displayMedia.length - 1 : currentIndex - 1;
                setEnteringFromLeft(prevIndex);
                
                // Check if the previous card has latched on to the top position
                if (progress > cardLatchThreshold && !hasCardLatched.current) {
                    hasCardLatched.current = true;
                    
                    // If current card is video, register that it will need to be reset
                    const mediaItem = displayMedia[currentIndex];
                    if (mediaItem?.type === 'Video') {
                        // Mark this video for complete reset when returning
                        lastVideoStates.current[currentIndex] = 'reset';
                    }
                }
            } else {
                setEnteringFromLeft(null);
            }
            
            // Current media item
            const mediaItem = displayMedia[currentIndex];
            const isCurrentVideo = mediaItem?.type === 'Video';
            
            // Precisely handle 20% threshold for video playback
            if (isCurrentVideo) {
                if (progress >= swipePauseThreshold) {
                    // Pause video when we cross the threshold
                    if (isVideoPlaying[currentIndex]) {
                        setIsVideoPlaying(prev => ({
                            ...prev,
                            [currentIndex]: false
                        }));
                        // Only mark it as 'playing before' if we haven't marked it for reset
                        if (lastVideoStates.current[currentIndex] !== 'reset') {
                            wasVideoPaused.current = true;
                            lastVideoStates.current[currentIndex] = true;
                        }
                    }
                } else if (progress < swipePauseThreshold) {
                    // Auto-resume if we go back below threshold and video was playing before
                    // If it was marked for reset, we'll handle that in the touch end handler
                    if (!isVideoPlaying[currentIndex] && 
                        (lastVideoStates.current[currentIndex] === true || wasVideoPaused.current) &&
                        !isLowPowerMode) {
                        // Direction changed OR progress returned below threshold - resume video
                        setIsVideoPlaying(prev => ({
                            ...prev,
                            [currentIndex]: true
                        }));
                        
                        // Clear the flag once we've resumed
                        if (wasVideoPaused.current && progress === 0) {
                            wasVideoPaused.current = false;
                        }
                    }
                }
            }
            
            // If swipe is almost back to origin (indicating user changed their mind)
            if (Math.abs(diff) < 15 && directionChanged) {
                // For videos, ensure we resume if it was paused during this swipe
                if (isCurrentVideo && wasVideoPaused.current && !isLowPowerMode) {
                    setIsVideoPlaying(prev => ({
                        ...prev,
                        [currentIndex]: true
                    }));
                    wasVideoPaused.current = false;
                }
            }
        }
    }, [currentIndex, displayMedia, isVideoPlaying, swipePauseThreshold, cardLatchThreshold, displayMedia.length, isLowPowerMode]);

    const handleMouseUp = useCallback(() => {
        // Skip handling for single items
        if (displayMedia.length <= 1) return;
        
        // Reset swipe direction tracking
        previousSwipeDirection.current = null;
        wasVideoPaused.current = false;

        // Only handle swipe actions if we actually detected swiping
        if (wasSwiping.current) {
            // Don't pause all videos when swiping ends without slide change

            const diff = mouseStartX.current - mouseEndX.current;

            if (diff > 50) {
                // Set transition flag for smooth animation
                setTransitionInProgress(true);

                // Complete left swipe animation before changing slide
                const timer = setTimeout(() => {
                    nextSlide();
                    setSwipeDirection(null);
                    setSwipeProgress(0);
                    // Reset transition flag after animation completes
                    setTimeout(() => setTransitionInProgress(false), 75);
                }, 50);
                return () => clearTimeout(timer);
            } else if (diff < -50) {
                // Set transition flag for smooth animation
                setTransitionInProgress(true);

                // Complete right swipe animation before changing slide
                const timer = setTimeout(() => {
                    prevSlide();
                    setSwipeDirection(null);
                    setSwipeProgress(0);
                    // Reset transition flag after animation completes
                    setTimeout(() => setTransitionInProgress(false), 75);
                }, 50);
                return () => clearTimeout(timer);
            } else {
                // Swipe ended without changing slides - restore video state
                const mediaItem = displayMedia[currentIndex];
                if (mediaItem?.type === 'Video') {
                    // If video was playing before swipe, ensure it's still playing (unless in low power mode)
                    if (lastVideoStates.current[currentIndex] === true && !isLowPowerMode) {
                        setIsVideoPlaying(prev => ({
                            ...prev,
                            [currentIndex]: true
                        }));
                    }
                    
                    // Clear any reset flags - we're not going to reset the video
                    if (lastVideoStates.current[currentIndex] === 'reset') {
                        lastVideoStates.current[currentIndex] = true;
                    }
                }
            }
        }
        
        // Reset the card latch flag
        hasCardLatched.current = false;
        wasSwiping.current = false;
        
        isMouseDown.current = false;
        setGrabbing(false);
        setIsSwiping(false);
        setSwipeDirection(null);
        setSwipeProgress(0);
        setEnteringFromLeft(null);
    }, [isVideoPlaying, nextSlide, prevSlide, currentIndex, displayMedia, isSwiping, displayMedia.length, isLowPowerMode]);

    const handleMouseLeave = useCallback(() => {
        if (isMouseDown.current) {
            isMouseDown.current = false;
            setGrabbing(false);
            setIsSwiping(false);
            setSwipeDirection(null);
            setSwipeProgress(0);
            setEnteringFromLeft(null);
        }
    }, []);

    // Add event listener for global mouse up to catch releases outside the component
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isMouseDown.current) {
                handleMouseUp();
            }
        };

        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [handleMouseUp]);  // Add handleMouseUp to dependencies

    // Memoize getVisibleCards to prevent recalculation on every render
    const getVisibleCards = useCallback(() => {
        const cards = [];
        const total = displayMedia.length;

        // Special case for single item - just show it
        if (total <= 1) {
            if (total === 1) {
                cards.push(0);
            }
            return cards;
        }
        
        // Special case for exactly 2 items - show one at front, one at back
        if (total === 2) {
            if (swipeDirection === 'right' && enteringFromLeft !== null) {
                // When swiping right, show both with previous entering
                cards.push(enteringFromLeft);
                cards.push(currentIndex);
            } else if (swipeDirection === 'left') {
                // When swiping left, show current and next
                cards.push(currentIndex);
                cards.push((currentIndex + 1) % total);
            } else {
                // Normal state - show current at front, other at back
                cards.push(currentIndex);
                cards.push((currentIndex + 1) % total);
            }
            return cards;
        }

        // For 3+ items, use existing logic
        if (isSingleLook) {
            // In single look mode, only show the current card
            if (total > 0) {
                if (swipeDirection === 'right' && enteringFromLeft !== null) {
                    // When swiping right, show the previous card entering from left
                    cards.push(enteringFromLeft);
                    // Also include current card for animation
                    cards.push(currentIndex);
                } else if (swipeDirection === 'left') {
                    // When swiping left, show current card and next card
                    cards.push(currentIndex);
                    if (total > 1) {
                        cards.push((currentIndex + 1) % total);
                    }
                } else {
                    // Normal state - only show current card
                    cards.push(currentIndex);
                }
            }
        } else {
            // Modified behavior for stacked mode with 3+ items
            if (swipeDirection === 'right' && enteringFromLeft !== null) {
                // When swiping right, show the previous card entering from left
                cards.push(enteringFromLeft);
                // Still show the current card (moving right)
                cards.push(currentIndex);
                // Add one more card if available and we have more than 2 items
                if (total > 2) {
                    cards.push((currentIndex + 1) % total);
                }
            } else {
                // Normal case - show all cards up to 3
                const maxCards = Math.min(3, total);
                for (let i = 0; i < maxCards; i++) {
                    if (total > 0) {
                        cards.push((currentIndex + i) % total);
                    }
                }
            }
        }

        return cards;
    }, [currentIndex, displayMedia.length, enteringFromLeft, isSingleLook, swipeDirection]);

    // Toggle video playing state - improved to work better with tap vs swipe detection
    const toggleVideoPlay = useCallback((index, e) => {
        // Ensure we stop propagation to prevent any parent handlers from firing
        e.stopPropagation();
        e.preventDefault();
        
        // IMPORTANT: This function should only be triggered by explicit button clicks
        // Check if this was a clean button click (no swiping)
        if (isSwiping || wasSwiping.current) return;

        const mediaItem = displayMedia[index];
        if (mediaItem?.type === 'Video') {
            // Only toggle if this is a direct control button click
            const isControlButton = 
                e.target.closest('.swiper-card__play-button') || 
                e.target.closest('.swiper-card__play-button-lowpower') ||
                e.target.closest('.swiper-card__pause-button');

            if (isControlButton) {
                // Set shouldAutoPlay for this specific video when manually played
                setShouldAutoPlay(prev => ({
                    ...prev,
                    [index]: !isVideoPlaying[index]
                }));
                
                setIsVideoPlaying(prev => ({
                    ...prev,
                    [index]: !prev[index]
                }));
                
                // If we're turning video on, clear last video states to prevent auto-resume issues
                if (!isVideoPlaying[index]) {
                    lastVideoStates.current[index] = false;
                }
            }
        }
    }, [displayMedia, isSwiping, isVideoPlaying]);

    // Handle video ready state
    const handleVideoReady = useCallback((index) => {
        setVideosLoaded(prev => ({
            ...prev,
            [index]: true
        }));
    }, []);

    // Memoize visible cards to prevent recalculation on every render
    const visibleCards = useMemo(() => getVisibleCards(), 
        [getVisibleCards]);

    // Track which videos are currently mounted to handle reset behavior
    useEffect(() => {
        const newMountStates = {};
        visibleCards.forEach(mediaIndex => {
            const mediaItem = displayMedia[mediaIndex];
            if (mediaItem?.type === 'Video') {
                // If this is a previously unmounted video that's now mounted
                if (!videoMountStates[mediaIndex] && mediaIndex === currentIndex) {
                    // Reset the video by setting to false first
                    setIsVideoPlaying(prev => ({
                        ...prev,
                        [mediaIndex]: false
                    }));
                    
                    // Don't auto-play when remounting - let the user decide to play
                    // Only auto-play if explicitly set via shouldAutoPlay state
                    if (!isLowPowerMode && shouldAutoPlay[mediaIndex]) {
                        setTimeout(() => {
                            setIsVideoPlaying(prev => ({
                                ...prev,
                                [mediaIndex]: true
                            }));
                        }, 50);
                    }
                }
                newMountStates[mediaIndex] = true;
            }
        });
        
        // Detect unmounted videos
        Object.keys(videoMountStates).forEach(index => {
            if (!newMountStates[parseInt(index)]) {
                // Clear any saved state for unmounted videos
                delete lastVideoStates.current[index];
            }
        });
        
        setVideoMountStates(newMountStates);
    }, [visibleCards, displayMedia, currentIndex, videoMountStates, isLowPowerMode, shouldAutoPlay]);

    // Reset last video states when currentIndex changes
    useEffect(() => {
        // Clear any stored video states when slide changes
        lastVideoStates.current = {};
        
        // Clear shouldAutoPlay for videos that are no longer current
        setShouldAutoPlay(prev => {
            const newState = { ...prev };
            Object.keys(newState).forEach(index => {
                if (parseInt(index) !== currentIndex) {
                    delete newState[index];
                }
            });
            return newState;
        });
    }, [currentIndex]);

    // Reset video when fully swiped away
    useEffect(() => {
        if (!transitionInProgress) return;
        
        // Store previous index to reset its video state
        const prevIndex = prevItemRef.current;
        if (prevIndex !== null && prevIndex !== currentIndex) {
            const mediaItem = displayMedia[prevIndex];
            if (mediaItem?.type === 'Video') {
                // Reset video playing state for the previous index
                setIsVideoPlaying(prev => ({
                    ...prev,
                    [prevIndex]: false
                }));
            }
        }
    }, [transitionInProgress, currentIndex, displayMedia]);

    // Clear latch flag on component render or re-render
    useEffect(() => {
        hasCardLatched.current = false;
    }, []);

    const handlePDPNavigation = (product) => {
        // // Enhanced programmatic link creation for iPhone app compatibility
        // const a = document.createElement("a");
        // a.href = product.url;
        // a.target = '_blank';
        // a.rel = 'noopener noreferrer';
        // a.style.display = 'none';

        // // Add to DOM, click, then remove
        // document.body.appendChild(a);
        // a.click();
        // setTimeout(() => {
        // document.body.removeChild(a);
        // }, 100);
        window.open(product.url, "_blank", "noopener,noreferrer");
    };

    return (
        <div
            className={`swiper-card ${grabbing ? 'swiper-card--grabbing' : ''} ${displayMedia.length === 1 ? 'swiper-card--single-item' : ''}`}
            style={{
                cursor: displayMedia.length > 1 ? (grabbing ? 'grabbing' : 'grab') : 'default', // Don't use grab cursor for single items
            }}
            // Make sure clicks on card don't do anything
            onClick={(e) => {
                // Single click on the card - explicitly do nothing
                if (!wasSwiping.current) {
                    e.stopPropagation();
                    e.preventDefault();
                }
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
        >
            <div className="swiper-card__cards-container">
                {displayMedia.length > 0 ? (
                    visibleCards.map((mediaIndex, stackPosition) => {
                        // Adjusted scale values based on number of items
                        const baseScale = isSingleLook ? 1 : (
                            displayMedia.length === 1 ? 1 : // Full size for single item
                            displayMedia.length === 2 ? (stackPosition === 0 ? 1 : 0.95) : // Two items
                            (stackPosition === 0 ? 1 : // Regular 3+ items
                             stackPosition === 1 ? 0.95 :
                             stackPosition === 2 ? 0.9 : 0.76)
                        );

                        // Adjusted horizontal offsets based on number of items
                        const baseOffset = isSingleLook ? 0 : (
                            displayMedia.length === 1 ? 0 : // No offset for single item
                            displayMedia.length === 2 ? (stackPosition === 0 ? 0 : 40) : // More offset for two items
                            stackPosition * 20 // Regular spacing for 3+ items
                        );

                        let finalScale = baseScale;
                        let horizontalOffset = baseOffset;

                        // Special case for single image - make it fill container width
                        if (displayMedia.length === 1) {
                            finalScale = 1; // Full size
                            horizontalOffset = 0; // Centered
                        }

                        // All cards fully opaque by default - no opacity for idle cards
                        let opacity = 1;

                        // Handle swipe animations
                        if (swipeDirection === 'left') {
                            const progress = swipeProgress;

                            if (stackPosition === 0) {
                                // Top card moves left and out (like top step of escalator)
                                horizontalOffset = baseOffset - (80 * progress);
                                opacity = Math.max(0, 1 - (progress * 3));
                                // Maintain scale as it moves off
                                finalScale = baseScale * 1.05;
                            }
                            else if (!isSingleLook && stackPosition === 1) {
                                // Second card moves to first position - FULLY scale up during swipe
                                horizontalOffset = baseOffset - (baseOffset * progress);
                                // Complete scaling (100% instead of 30%) for smooth escalator effect
                                finalScale = baseScale + ((1 - baseScale) * progress);
                            }
                            else if (!isSingleLook && stackPosition === 2) {
                                // Third card moves to second position - FULLY scale to second card size
                                horizontalOffset = baseOffset - ((baseOffset - 20) * progress);
                                // Complete scaling to next size (100% instead of 20%)
                                finalScale = baseScale + ((0.92 - baseScale) * progress);
                            }
                        }
                        else if (swipeDirection === 'right' && enteringFromLeft !== null) {
                            const progress = swipeProgress;

                            if (stackPosition === 0) {
                                // Previous card coming in from left - move into top position
                                horizontalOffset = -80 + (80 * progress);
                                opacity = Math.min(1, progress * 3);
                                finalScale = 1;
                            }
                            else if (stackPosition === 1) {
                                // Current card moving to second position
                                horizontalOffset = 0 + (20 * progress); // Subtle right movement
                                opacity = 1; // Keep fully visible

                                // Scale DOWN to second card size (like descending escalator step)
                                finalScale = 1 - ((1 - 0.92) * progress);
                            }
                            else if (!isSingleLook && stackPosition === 2) {
                                // Second card becoming third - more pronounced movement
                                horizontalOffset = baseOffset + (10 * progress);

                                // Scale DOWN to third card size
                                finalScale = 0.92 - ((0.92 - 0.84) * progress);
                            }
                        }

                        // Determine overlay opacity - adjusted for item count
                        const overlayOpacity = isSingleLook ? 0 : (
                            displayMedia.length === 1 ? 0 : // No overlay for single item
                            stackPosition === 0 ? 0 :
                            stackPosition === 1 ? 0.2 : 0.4
                        );

                        // Adjust overlay opacity during transitions - only needed in stacked mode
                        let finalOverlayOpacity = overlayOpacity;
                        if (!isSingleLook) {
                            if (swipeDirection === 'left') {
                                if (stackPosition === 1) {
                                    // Second card becoming top card - fade out overlay 3x faster
                                    finalOverlayOpacity = overlayOpacity * Math.max(0, 1 - (swipeProgress * 3));
                                } else if (stackPosition === 2) {
                                    // Third card becoming second card - adjust overlay 3x faster
                                    const targetChange = (0.4 - 0.2) * Math.min(1, swipeProgress * 3);
                                    finalOverlayOpacity = 0.4 - targetChange;
                                }
                            } else if (swipeDirection === 'right') {
                                // Similar logic for right swipe
                                if (stackPosition === 1) {
                                    finalOverlayOpacity = overlayOpacity * Math.max(0, 1 - (swipeProgress * 3));
                                } else if (stackPosition === 2) {
                                    const targetChange = (0.4 - 0.2) * Math.min(1, swipeProgress * 3);
                                    finalOverlayOpacity = 0.4 - targetChange;
                                }
                            }
                        }

                        // These styles need to remain inline as they're calculated dynamically
                        const cardStyle = {
                            zIndex: swipeDirection === 'right' && stackPosition === 0 ? 1000 : displayMedia.length - stackPosition,
                            transition: isSwiping ? 'none' : 'all 0.35s cubic-bezier(0.25, 0.1, 0.25, 1)',
                            transform: `translateX(${horizontalOffset}px) scale(${finalScale})`,
                            opacity: opacity,
                            // Add width adjustment for single images to fill container width
                            width: displayMedia.length === 1 ? '100%' : undefined,
                            maxWidth: displayMedia.length === 1 ? '100%' : undefined,
                        };

                        const mediaItem = displayMedia[mediaIndex];
                        const isVideo = mediaItem && mediaItem.type === 'Video';
                        // Check if this specific video should autoplay
                        const shouldAutoPlayValue = isVideo && mediaIndex === currentIndex && !isLowPowerMode && shouldAutoPlay[mediaIndex];

                        return (
                            <React.Fragment key={mediaIndex}>
                            {/* <a
                                href={product.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                // className="border-0 bg-transparent text-start product-titles cursor-pointer"
                                key={mediaIndex} 
                                className="swiper-card__card border-0 bg-transparent p-0" 
                                style={cardStyle}
                                // style={{ textDecoration: "none" }}
                                onClick={() => {
                                localStorage.setItem('singleLookScrollIndex', 0);
                                handlePDPNavigation(product);}}
                            > */}
                            <button 
                                name="pdpNavigation"
                                //key={mediaIndex} 
                                className="swiper-card__card border-0 bg-transparent p-0" 
                                style={cardStyle}
                                // Prevent card clicks from doing anything unless on control buttons
                                onClick={(e) => {
                                    //OnClick navigation for carousel images/video
                                    handlePDPNavigation(product);                                                             
                                    // Only allow clicks through for control elements
                                    const isControlElement = 
                                        e.target.closest('.swiper-card__play-button') || 
                                        e.target.closest('.swiper-card__play-button-lowpower') ||
                                        e.target.closest('.swiper-card__pause-button');
                                        
                                    if (!isControlElement) {
                                        // Block any action for clicks on the card itself
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }
                                }}
                            >
                                {isVideo ? (
                                    <div className="swiper-card__video-container">
                                        <ReactPlayer
                                            url={mediaItem.url}
                                            playing={isVideoPlaying[mediaIndex] || false}
                                            loop={true}
                                            muted={true}
                                            playsinline={true}
                                            width="100%"
                                            height="100%"
                                            style={{ objectFit: 'cover' }}
                                            ref={player => {
                                                // Store reference to access video methods if needed
                                                if (player) {
                                                    videoPlayerRefs.current[mediaIndex] = player;
                                                }
                                            }}
                                            config={{
                                                file: {
                                                    attributes: {
                                                        style: {
                                                            objectFit: 'cover',
                                                            width: '100%',
                                                            height: '100%'
                                                        },
                                                        playsInline: true,
                                                        autoPlay: shouldAutoPlay[mediaIndex] && !isLowPowerMode
                                                    }
                                                },
                                                youtube: {
                                                    playerVars: {
                                                        modestbranding: 1
                                                    }
                                                }
                                            }}
                                            onPlay={() => {
                                                if (!hasPlayedOnce[mediaIndex]) {
                                                    setHasPlayedOnce(prev => ({ ...prev, [mediaIndex]: true }));
                                                }
                                            }}
                                            onReady={() => {
                                                // Mark this video as loaded
                                                handleVideoReady(mediaIndex);
                                                
                                                // Only auto-play if explicitly set via shouldAutoPlay for this specific video
                                                if (shouldAutoPlay[mediaIndex] && !isLowPowerMode) {
                                                    // Delay auto-play slightly to ensure clean playback from start
                                                    setTimeout(() => {
                                                        setIsVideoPlaying(prev => ({
                                                            ...prev,
                                                            [mediaIndex]: true
                                                        }));
                                                    }, 50);
                                                }
                                            }}
                                        />
                                        <div 
                                            className="swiper-card__video-control-overlay"
                                            // Remove the click handler from the overlay - only the buttons should respond
                                            // Overlay should be clickable only for swipes
                                            style={{ pointerEvents: 'none' }} // Make overlay transparent to clicks
                                        >
                                            {/* Show play button only when video is paused AND loaded */}
                                            {!isVideoPlaying[mediaIndex] && videosLoaded[mediaIndex] && (
                                                <div
                                                    className={
                                                        (isLowPowerMode && !hasPlayedOnce[mediaIndex])
                                                            ? "swiper-card__play-button-lowpower"
                                                            : "swiper-card__play-button"
                                                    }
                                                    style={{
                                                        // opacity: 0.8,
                                                        zIndex: 100,
                                                        pointerEvents: 'auto', // Re-enable pointer events for play button
                                                    }}
                                                    onClick={(e) => toggleVideoPlay(mediaIndex, e)}
                                                    onTouchEnd={(e) => {
                                                        // Only toggle if it's a clean tap, not a swipe
                                                        if (!isSwiping && !wasSwiping.current) {
                                                            toggleVideoPlay(mediaIndex, e);
                                                        }
                                                    }}
                                                >
                                                    <div className="" >
                                                        <img src="/static/assets/play.svg" alt="play"/>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Show pause button only when video is playing AND loaded */}
                                            {isVideoPlaying[mediaIndex] && videosLoaded[mediaIndex] && (
                                                <div
                                                    className="swiper-card__pause-button"
                                                    style={{
                                                        // opacity: 0.8, 
                                                        zIndex: 100,
                                                        pointerEvents: 'auto', // Re-enable pointer events for pause button
                                                    }}
                                                    onClick={(e) => toggleVideoPlay(mediaIndex, e)}
                                                    onTouchEnd={(e) => {
                                                        // Only toggle if it's a clean tap, not a swipe
                                                        if (!isSwiping && !wasSwiping.current) {
                                                            toggleVideoPlay(mediaIndex, e);
                                                        }
                                                    }}
                                                >
                                                    <div className="">
                                                        <img src="/static/assets/pause.svg" alt="pause"/>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {finalOverlayOpacity > 0 && (
                                            <div
                                                className="swiper-card__overlay"
                                                style={{
                                                    opacity: finalOverlayOpacity,
                                                    transition: isSwiping ? 'none' : 'opacity 0.05s cubic-bezier(0.25, 0.1, 0.25, 1)'
                                                }}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div className="swiper-card__image-container">
                                        <img
                                            className="swiper-card__image"
                                            src={mediaItem.url}
                                            alt={`Product ${mediaIndex + 1}`}
                                        />

                                        {finalOverlayOpacity > 0 && (
                                            <div
                                                className="swiper-card__overlay"
                                                style={{
                                                    opacity: finalOverlayOpacity,
                                                    transition: isSwiping ? 'none' : 'opacity 0.05s cubic-bezier(0.25, 0.1, 0.25, 1)'
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </button>
                            {/* </a> */}
                            </React.Fragment>
                        );
                    })
                ) : (
                    <div className="swiper-card__empty-state">No images available</div>
                )}
            </div>

            {/* Pagination dots - currently commented out */}
        </div>
    );
};

SwiperCard.propTypes = SwiperCardProps;

export default React.memo(SwiperCard);